import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";

const PROPERTY_TYPES = [
  { group: "Residenziale", types: ["Appartamento", "Villa", "Edificio/Palazzo", "Monolocale", "Bilocale", "Trilocale", "Attico", "Loft", "Casa indipendente"] },
  { group: "Commerciale", types: ["Ufficio", "Negozio", "Capannone"] },
];
const STATUSES = [
  { value: "new", label: "Nuovo" },
  { value: "interesting", label: "Interessante" },
  { value: "contacted", label: "Contattato" },
  { value: "discarded", label: "Scartato" },
];

export default function FilterBar({ filters, portals, onApplyFilters }) {
  const [local, setLocal] = useState({ ...filters });

  const update = (key, value) => {
    const next = { ...local, [key]: value };
    setLocal(next);
    onApplyFilters(next);
  };

  const clearAll = () => {
    const cleared = { zone: "", price_min: null, price_max: null, property_type: "", portal: "", status: "", agent: "", show_affitto: false };
    setLocal(cleared);
    onApplyFilters(cleared);
  };

  const hasFilters = local.zone || local.price_min || local.price_max || local.property_type || local.portal || local.status || local.show_affitto;

  return (
    <div data-testid="filter-bar" className="border border-zinc-200 rounded-sm p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <div className="flex items-center gap-4">
          <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Filtri</span>
          {/* Affitto toggle */}
          <div className="flex items-center gap-1.5">
            <Switch
              data-testid="toggle-affitto"
              checked={local.show_affitto || false}
              onCheckedChange={(v) => update("show_affitto", v)}
              className="h-4 w-7"
            />
            <Label className="text-xs text-zinc-500 cursor-pointer font-medium">Mostra affitti</Label>
          </div>
        </div>
        {hasFilters && (
          <Button
            data-testid="clear-filters-btn"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="h-6 text-xs text-zinc-400 hover:text-zinc-700 px-2"
          >
            <X className="w-3 h-3 mr-1" /> Rimuovi
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {/* Zone */}
        <Input
          data-testid="filter-zone-input"
          placeholder="Zona..."
          value={local.zone}
          onChange={(e) => update("zone", e.target.value)}
          className="h-9 rounded-sm border-zinc-300 text-sm"
        />

        {/* Price min */}
        <Input
          data-testid="filter-price-min"
          type="number"
          placeholder="Prezzo min"
          value={local.price_min || ""}
          onChange={(e) => update("price_min", e.target.value ? parseInt(e.target.value) : null)}
          className="h-9 rounded-sm border-zinc-300 text-sm"
        />

        {/* Price max */}
        <Input
          data-testid="filter-price-max"
          type="number"
          placeholder="Prezzo max"
          value={local.price_max || ""}
          onChange={(e) => update("price_max", e.target.value ? parseInt(e.target.value) : null)}
          className="h-9 rounded-sm border-zinc-300 text-sm"
        />

        {/* Property type */}
        <Select value={local.property_type} onValueChange={(v) => update("property_type", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-type-select" className="h-9 rounded-sm border-zinc-300 text-sm">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {PROPERTY_TYPES.map(group => (
              <div key={group.group}>
                <div className="px-2 py-1 text-[10px] uppercase font-bold text-zinc-400 tracking-wider">{group.group}</div>
                {group.types.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>

        {/* Portal */}
        <Select value={local.portal} onValueChange={(v) => update("portal", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-portal-select" className="h-9 rounded-sm border-zinc-300 text-sm">
            <SelectValue placeholder="Portale" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {portals.filter(p => p.active).map(p => (
              <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status */}
        <Select value={local.status} onValueChange={(v) => update("status", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status-select" className="h-9 rounded-sm border-zinc-300 text-sm">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
